/**
 * Yerel depolama yardımcı işlevleri
 * Merkeziyetsiz kimlik ve 2FA entegrasyonu için genişletilmiş
 */
import { STORAGE_KEYS, APP_SETTINGS, TWO_FA_SETTINGS, SECURITY_LEVELS } from './constants';

/**
 * Local Storage'a veri kaydet
 * @param {string} key - Depolama anahtarı
 * @param {*} value - Depolanacak değer (otomatik olarak JSON'a çevrilir)
 */
export const saveToStorage = (key, value) => {
  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
    return true;
  } catch (error) {
    console.error('Veriler local storage\'a kaydedilemedi:', error);
    return false;
  }
};

/**
 * Local Storage'dan veri oku
 * @param {string} key - Depolama anahtarı
 * @param {*} defaultValue - Veri bulunamazsa dönecek varsayılan değer
 * @returns {*} Depolanan değer veya varsayılan değer
 */
export const getFromStorage = (key, defaultValue = null) => {
  try {
    const serializedValue = localStorage.getItem(key);
    if (serializedValue === null) {
      return defaultValue;
    }
    return JSON.parse(serializedValue);
  } catch (error) {
    console.error('Veriler local storage\'dan okunamadı:', error);
    return defaultValue;
  }
};

/**
 * Local Storage'dan veri sil
 * @param {string} key - Silinecek depolama anahtarı
 * @returns {boolean} Silme işleminin başarılı olup olmadığı
 */
export const removeFromStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Veriler local storage\'dan silinemedi:', error);
    return false;
  }
};

/**
 * Tüm yerel depolama verilerini temizle
 * @returns {boolean} Temizleme işleminin başarılı olup olmadığı
 */
export const clearStorage = () => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Local storage temizlenemedi:', error);
    return false;
  }
};

/**
 * Kullanıcı verisini kaydet
 * @param {Object} userData - Kaydedilecek kullanıcı verisi
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const saveUserData = (userData) => {
  return saveToStorage(STORAGE_KEYS.USER, userData);
};

/**
 * Kullanıcı verisini al
 * @returns {Object|null} Kullanıcı verisi veya null
 */
export const getUserData = () => {
  return getFromStorage(STORAGE_KEYS.USER);
};

/**
 * Kullanıcı verisini sil (çıkış yapma)
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const clearUserData = () => {
  removeFromStorage(STORAGE_KEYS.USER);
  removeFromStorage(STORAGE_KEYS.AUTH_TOKEN);
  return true;
};

/**
 * Kimlik doğrulama token'ını kaydet
 * @param {string} token - JWT token
 * @param {number} expiresIn - Token geçerlilik süresi (milisaniye)
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const saveAuthToken = (token, expiresIn = APP_SETTINGS.TOKEN_EXPIRATION) => {
  const tokenData = {
    value: token,
    expires: Date.now() + expiresIn
  };
  return saveToStorage(STORAGE_KEYS.AUTH_TOKEN, tokenData);
};

/**
 * Kimlik doğrulama token'ını al
 * @returns {string|null} JWT token veya null (süresi dolduysa null döner)
 */
export const getAuthToken = () => {
  const tokenData = getFromStorage(STORAGE_KEYS.AUTH_TOKEN);
  
  if (!tokenData) return null;
  
  // Token süresi dolmuşsa temizle ve null döndür
  if (tokenData.expires && tokenData.expires < Date.now()) {
    removeFromStorage(STORAGE_KEYS.AUTH_TOKEN);
    return null;
  }
  
  return tokenData.value;
};

/**
 * Token'ın geçerlilik süresini kontrol et
 * @returns {boolean} Token'ın geçerli olup olmadığı
 */
export const isTokenValid = () => {
  const tokenData = getFromStorage(STORAGE_KEYS.AUTH_TOKEN);
  
  if (!tokenData || !tokenData.value) return false;
  
  // Token süresi dolmuşsa false döndür
  if (tokenData.expires && tokenData.expires < Date.now()) {
    return false;
  }
  
  return true;
};

/**
 * Uygulama temasını kaydet
 * @param {string} theme - Tema ('light', 'dark', 'system')
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const saveTheme = (theme) => {
  return saveToStorage(STORAGE_KEYS.THEME, theme);
};

/**
 * Uygulama temasını al
 * @returns {string} Tema ('light', 'dark', 'system')
 */
export const getTheme = () => {
  return getFromStorage(STORAGE_KEYS.THEME, APP_SETTINGS.DEFAULT_THEME);
};

/**
 * Dil tercihini kaydet
 * @param {string} locale - Dil kodu ('tr', 'en', vb.)
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const saveLocale = (locale) => {
  return saveToStorage(STORAGE_KEYS.LOCALE, locale);
};

/**
 * Dil tercihini al
 * @returns {string} Dil kodu ('tr', 'en', vb.)
 */
export const getLocale = () => {
  return getFromStorage(STORAGE_KEYS.LOCALE, APP_SETTINGS.DEFAULT_LANGUAGE);
};

/**
 * Biyometrik doğrulama durumunu kaydet
 * @param {boolean} enabled - Biyometrik doğrulama etkin mi?
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const saveBiometricStatus = (enabled) => {
  return saveToStorage(STORAGE_KEYS.BIOMETRIC_ENABLED, enabled);
};

/**
 * Biyometrik doğrulama durumunu al
 * @returns {boolean} Biyometrik doğrulama etkin mi?
 */
export const getBiometricStatus = () => {
  return getFromStorage(STORAGE_KEYS.BIOMETRIC_ENABLED, true);
};

/**
 * Güvenlik seviyesini kaydet
 * @param {string} level - Güvenlik seviyesi ('low', 'medium', 'high', 'maximum')
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const saveSecurityLevel = (level) => {
  return saveToStorage(STORAGE_KEYS.SECURITY_LEVEL, level);
};

/**
 * Güvenlik seviyesini al
 * @returns {string} Güvenlik seviyesi ('low', 'medium', 'high', 'maximum')
 */
export const getSecurityLevel = () => {
  return getFromStorage(STORAGE_KEYS.SECURITY_LEVEL, APP_SETTINGS.DEFAULT_SECURITY_LEVEL);
};

/**
 * Son aktif zamanı kaydet
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const updateLastActive = () => {
  return saveToStorage(STORAGE_KEYS.LAST_ACTIVE, Date.now());
};

/**
 * Son aktif zamanı al
 * @returns {number} Son aktif zaman (milisaniye cinsinden timestamp)
 */
export const getLastActive = () => {
  return getFromStorage(STORAGE_KEYS.LAST_ACTIVE, Date.now());
};

/**
 * İnaktif durum kontrolü
 * @param {number} timeout - İnaktif sayılması için geçmesi gereken süre (milisaniye)
 * @returns {boolean} Kullanıcının inaktif olup olmadığı
 */
export const isUserInactive = (timeout = APP_SETTINGS.INACTIVITY_TIMEOUT) => {
  const lastActive = getLastActive();
  return Date.now() - lastActive > timeout;
};

/**
 * Belirtilen bir kullanıcı tercihini kaydet
 * @param {string} preferenceName - Tercih adı
 * @param {*} value - Tercih değeri
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const saveUserPreference = (preferenceName, value) => {
  const preferencesKey = `${STORAGE_KEYS.USER}_preferences`;
  const currentPreferences = getFromStorage(preferencesKey, {});
  
  return saveToStorage(preferencesKey, {
    ...currentPreferences,
    [preferenceName]: value
  });
};

/**
 * Belirtilen bir kullanıcı tercihini al
 * @param {string} preferenceName - Tercih adı
 * @param {*} defaultValue - Varsayılan değer
 * @returns {*} Tercih değeri
 */
export const getUserPreference = (preferenceName, defaultValue = null) => {
  const preferencesKey = `${STORAGE_KEYS.USER}_preferences`;
  const preferences = getFromStorage(preferencesKey, {});
  
  return preferences[preferenceName] !== undefined
    ? preferences[preferenceName]
    : defaultValue;
};

/**
 * Birden fazla kullanıcı tercihini toplu olarak kaydet
 * @param {Object} preferences - Kaydedilecek tercihler objesi
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const saveUserPreferences = (preferences) => {
  if (!preferences || typeof preferences !== 'object') {
    console.error('saveUserPreferences: preferences must be an object');
    return false;
  }

  const preferencesKey = `${STORAGE_KEYS.USER}_preferences`;
  const currentPreferences = getFromStorage(preferencesKey, {});
  
  return saveToStorage(preferencesKey, {
    ...currentPreferences,
    ...preferences
  });
};

/**
 * Tüm kullanıcı tercihlerini al
 * @param {Object} defaultPreferences - Varsayılan tercihler objesi
 * @returns {Object} Kullanıcı tercihleri
 */
export const getUserPreferences = (defaultPreferences = {}) => {
  const preferencesKey = `${STORAGE_KEYS.USER}_preferences`;
  const preferences = getFromStorage(preferencesKey, defaultPreferences);
  
  return preferences;
};

/**
 * 2FA yedek kodlarını kaydet
 * @param {Array} codes - Yedek kodlar dizisi
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const save2FABackupCodes = (codes) => {
  return saveToStorage(STORAGE_KEYS.TWO_FA_BACKUP_CODES, codes);
};

/**
 * 2FA yedek kodlarını al
 * @returns {Array} Yedek kodlar dizisi
 */
export const get2FABackupCodes = () => {
  return getFromStorage(STORAGE_KEYS.TWO_FA_BACKUP_CODES, []);
};

/**
 * 2FA kurtarma anahtarını kaydet
 * @param {string} recoveryKey - Kurtarma anahtarı
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const save2FARecoveryKey = (recoveryKey) => {
  return saveToStorage(STORAGE_KEYS.TWO_FA_RECOVERY_KEY, recoveryKey);
};

/**
 * 2FA kurtarma anahtarını al
 * @returns {string|null} Kurtarma anahtarı
 */
export const get2FARecoveryKey = () => {
  return getFromStorage(STORAGE_KEYS.TWO_FA_RECOVERY_KEY, null);
};

/**
 * Doğrulanmış cihazları kaydet
 * @param {Array} devices - Cihazlar dizisi
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const saveVerifiedDevices = (devices) => {
  return saveToStorage(STORAGE_KEYS.TWO_FA_VERIFIED_DEVICES, devices);
};

/**
 * Doğrulanmış cihazları al
 * @returns {Array} Cihazlar dizisi
 */
export const getVerifiedDevices = () => {
  return getFromStorage(STORAGE_KEYS.TWO_FA_VERIFIED_DEVICES, []);
};

/**
 * Doğrulanmış cihaz ekle
 * @param {Object} device - Eklenecek cihaz bilgisi
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const addVerifiedDevice = (device) => {
  const devices = getVerifiedDevices();
  
  // Aynı ID'ye sahip cihaz var mı kontrol et
  const existingDevice = devices.find(d => d.id === device.id);
  
  if (existingDevice) {
    // Cihaz zaten varsa güncelle
    const updatedDevices = devices.map(d => d.id === device.id ? {...d, ...device, lastUsed: new Date().toLocaleDateString()} : d);
    return saveVerifiedDevices(updatedDevices);
  } else {
    // Yeni cihaz ekle
    const newDevice = {
      ...device,
      lastUsed: new Date().toLocaleDateString()
    };
    return saveVerifiedDevices([...devices, newDevice]);
  }
};

/**
 * Doğrulanmış cihazı kaldır
 * @param {string} deviceId - Kaldırılacak cihaz ID'si
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const removeVerifiedDevice = (deviceId) => {
  const devices = getVerifiedDevices();
  const updatedDevices = devices.filter(device => device.id !== deviceId);
  
  // Cihaz listesi değişmediyse false döndür
  if (devices.length === updatedDevices.length) {
    return false;
  }
  
  return saveVerifiedDevices(updatedDevices);
};

/**
 * 2FA durumunu kaydet
 * @param {boolean} enabled - 2FA etkin mi?
 * @returns {boolean} İşlemin başarılı olup olmadığı
 */
export const save2FAStatus = (enabled) => {
  return saveToStorage(STORAGE_KEYS.TWO_FA_ENABLED, enabled);
};

/**
 * 2FA durumunu al
 * @returns {boolean} 2FA etkin mi?
 */
export const get2FAStatus = () => {
  return getFromStorage(STORAGE_KEYS.TWO_FA_ENABLED, false);
};

/**
 * Mevcut cihazı tanımla ve doğrulanmış cihazlara ekle
 * @returns {Object} Eklenen cihaz bilgisi
 */
export const registerCurrentDevice = () => {
  const isRTL = document.dir === 'rtl';
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Cihaz tipini algıla
  let deviceType = 'Bilinmeyen Cihaz';
  let deviceName = 'Cihaz';
  let deviceOS = 'Bilinmeyen';
  
  // Tarayıcı bilgisini algıla
  let browser = 'Bilinmeyen';
  
  if (userAgent.indexOf('mobile') !== -1) {
    deviceType = 'Mobil';
  } else if (userAgent.indexOf('tablet') !== -1) {
    deviceType = 'Tablet';
  } else {
    deviceType = 'Masaüstü';
  }
  
  // İşletim sistemini algıla
  if (userAgent.indexOf('android') !== -1) {
    deviceOS = 'Android';
  } else if (userAgent.indexOf('iphone') !== -1 || userAgent.indexOf('ipad') !== -1 || userAgent.indexOf('ipod') !== -1) {
    deviceOS = 'iOS';
  } else if (userAgent.indexOf('windows') !== -1) {
    deviceOS = 'Windows';
  } else if (userAgent.indexOf('mac') !== -1) {
    deviceOS = 'macOS';
  } else if (userAgent.indexOf('linux') !== -1) {
    deviceOS = 'Linux';
  }
  
  // Tarayıcıyı algıla
  if (userAgent.indexOf('chrome') !== -1 && userAgent.indexOf('edg') === -1) {
    browser = 'Chrome';
  } else if (userAgent.indexOf('firefox') !== -1) {
    browser = 'Firefox';
  } else if (userAgent.indexOf('safari') !== -1 && userAgent.indexOf('chrome') === -1) {
    browser = 'Safari';
  } else if (userAgent.indexOf('edg') !== -1) {
    browser = 'Edge';
  } else if (userAgent.indexOf('opera') !== -1 || userAgent.indexOf('opr') !== -1) {
    browser = 'Opera';
  }
  
  // Cihaz adını oluştur
  deviceName = `${browser} - ${deviceOS}`;
  
  // Cihaz bilgilerini oluştur
  const device = {
    id: `device-${Date.now()}`,
    name: deviceName,
    type: deviceType,
    os: deviceOS,
    browser: browser,
    lastUsed: new Date().toLocaleDateString(),
    isRTL: isRTL
  };
  
  // Cihazı doğrulanmış cihazlara ekle
  addVerifiedDevice(device);
  
  return device;
};